import PropTypes from 'prop-types';
import React from 'react';

import XYChart from './XYChart.jsx';
import {ChartBaseContent} from './Chart.jsx';

import * as d3time from 'd3-time';
import {scalePoint} from 'd3-scale';
import {area as d3Area, stack as d3Stack, line as d3Line, curveMonotoneX, stackOrderReverse} from 'd3-shape';

import {looseParseTime, format as formatTime, timeformats} from '../lib/TimeUtils';

const d3RangeFns = {
    minute: d3time.utcMinutes,
    hour: d3time.utcHours,
    day: d3time.utcDays,
    month: d3time.utcMonths,
    week: d3time.utcMondays,
    year: d3time.utcYears
};

const ascending = (a, b) => a < b ? -1 : a > b ? 1 : a >= b ? 0 : NaN;

class StackedLines extends ChartBaseContent {
    static displayName = 'StackedLines';

    doDraw() {
        super.doDraw();

        let {yScale, xScale, colors, data, type, hoveredItem, granularity, groups} = this.props;

        groups = groups.length > 0 ? groups : [{}];

        let {width, height} = this.getSize();

        let yKey = data.keys("y"),
            xKey = data.keys("x"),
            groupBy = data.keys("group"),
            items = data.values();


        let ctx = this.getCtx();

        if (items.length === 0) {
            return;
        }

        ctx.save();
        ctx.beginPath();

        const parseName = type == "time" ? looseParseTime : str => str;
        const getY = value => Math.round(yScale(value) * height);
        const getX = name => Math.round(xScale(parseName(name)) * width);

        let points = new Set();

        //generate names for missing data points
        if (type === "time") {
            if (d3RangeFns[granularity]) {
                let format = formatTime(timeformats[granularity] || timeformats["day"]);

                d3RangeFns[granularity](xScale.domain()[0], xScale.domain()[1]).forEach(function(t) {
                    points.add(format(t));
                });
            }
        } else if (type === "numeric") {
            for (let i = Number(xScale.domain()[0]); i <= xScale.domain()[1]; i++) {
                points.add(String(i));
            }
        } else {
            items.forEach(item => points.add(item[xKey]));
            points = [...points].sort(ascending);
        }

        points = [...points];

        let pointValues = {};
        let groupValues = {};

        for (let group of data.collapse("x").values()) {
            groupValues[group[groupBy]] = group[yKey];
        }

        for (let name of points) {
            pointValues[name] = {x: getX(name)};
        }

        items.forEach(item => {
            let name = item[xKey],
                group = item[groupBy];

            pointValues[name][group] = parseFloat(item[yKey] || 0);
        });

        let stackGenerator = d3Stack()
            .keys(groups.map(g => g.name))
            .order(stackOrderReverse)
            .value((d, key) => d[key] || 0);

        let drawArea = d3Area()
            .x(d => d.data.x)
            .y0(d => getY(d[0]))
            .y1((d, _, {key}) => {
                let y1 = getY(d[1]),
                    y0 = getY(d[0]);
                if (hoveredItem && hoveredItem[groupBy] == key) {
                    if (y0 - y1 < 2) {
                        return y0 - 2;
                    }
                }

                return y1;
            })
            .context(ctx)
            .curve(curveMonotoneX);


        let stacked = stackGenerator(Object.values(pointValues));

        for (let row of stacked) {
            let group = row.key;
            ctx.beginPath();
            ctx.fillStyle = colors.get(group);
            drawArea(row);
            ctx.fill();
        }

        ctx.restore();

        this._drawHoveredItems = (ctx, hoveredItem) => {
            for (let row of stacked) {
                let group = row.key;
                if (hoveredItem[groupBy] == group) {
                    ctx.beginPath();
                    ctx.fillStyle = colors.get(group);
                    drawArea.context(ctx)(row);
                    ctx.fill();
                    break;
                }
            }
        };

        this._reverseLookup = (x, y) => {
            let xIndex0 = null, xIndex1 = null;
            for (let i = 1; i <= points.length; i++) {
                if (getX(points[i]) >= x && points[i - 1]) {
                    xIndex0 = i - 1;
                    xIndex1 = i;
                    break;
                }
            }

            if (xIndex0 !== null) {
                let getDelta = (x, x0, x1) => (x - x0) / (x1 - x0);
                let xdelta = getDelta(x, getX(points[xIndex0]), getX(points[xIndex1]));

                let interpolate = (x0, x1, delta) => x0 * (1 - delta) + x1 * delta;
                let interpolateRange = (pos0, pos1, delta) => (
                    [
                        interpolate(pos0[0], pos1[0], delta),
                        interpolate(pos0[1], pos1[1], delta)
                    ]);

                let yInverse = yScale.invert(y / height);

                for (let row of stacked) {
                    let interpolated = interpolateRange(row[xIndex0], row[xIndex1], xdelta);
                    if (yInverse >= interpolated[0] && yInverse <= interpolated[1]) {
                        let item = row.key ? {[groupBy]: row.key} : {};
                        item[yKey] = groupValues[row.key];
                        return {item};
                    }
                }
            }

            return null;
        };
    }
}


class OverlappingLines extends ChartBaseContent {
    static displayName = 'OverlappingLines';

    doDraw() {
        super.doDraw();

        let {yScale, xScale, colors, data, type, granularity} = this.props;

        let {width, height} = this.getSize();

        let yKey = data.keys("y"),
            xKey = data.keys("x"),
            groupBy = data.keys("group"),
            items = data.values();


        let ctx = this.getCtx();

        if (items.length === 0) {
            return;
        }

        ctx.save();

        let LINE_WIDTH = 2;
        ctx.lineWidth = LINE_WIDTH;

        const parseName = type == "time" ? looseParseTime : str => str;
        const getY = value => Math.round(yScale(value) * height);
        const getX = name => Math.round(xScale(parseName(name)) * width);

        let points = new Set();

        //generate names for missing data points
        if (type === "time") {
            if (d3RangeFns[granularity]) {
                let format = formatTime(timeformats[granularity] || timeformats["day"]);

                d3RangeFns[granularity](xScale.domain()[0], xScale.domain()[1]).forEach(function(t) {
                    points.add(format(t));
                });
            }
        } else if (type === "numeric") {
            for (let i = Number(xScale.domain()[0]); i <= xScale.domain()[1]; i++) {
                points.add(String(i));
            }
        } else {
            items.forEach(item => points.add(item[xKey]));
            points = [...points].sort(ascending);
        }

        points = [...points];

        let grouped = {};

        items.forEach(item => {
            let name = item[xKey],
                group = item[groupBy],
                value = parseFloat(item[yKey] || 0);

            if (!grouped[group]) {
                grouped[group] = {};
            }

            grouped[group][name] = value;
        });

        let lines = [];

        for (let group of Object.keys(grouped)) {
            let line = points.map(point => {
                let value = grouped[group][point] || 0;
                return {
                    name: point,
                    value,
                    x: getX(point),
                    y: getY(value)
                };
            });

            line.group = group;
            lines.push(line);
        }

        let drawLine = d3Line()
            .y(d => d.y)
            .x(d => d.x)
            .context(ctx)
            .curve(curveMonotoneX);


        for (let line of lines) {
            let group = line.group;
            ctx.beginPath();
            ctx.strokeStyle = colors.get(group);
            drawLine(line);
            ctx.stroke();
        }

        ctx.closePath();
        ctx.restore();

        this._drawHoveredItems = (ctx, hoveredItem) => {
            ctx.lineWidth = LINE_WIDTH;

            for (let line of lines) {
                let group = line.group;
                if (String(hoveredItem[groupBy]) == String(group)) {
                    ctx.strokeStyle = colors.get(group);
                    ctx.fillStyle = colors.get(group);

                    for (let point of line) {
                        ctx.beginPath();
                        let r = hoveredItem[xKey] == point.name ? 3 : 1;

                        ctx.arc(point.x, point.y, r, 0, Math.PI * 2);
                        ctx.stroke();
                    }

                    ctx.beginPath();
                    drawLine.context(ctx)(line);
                    ctx.stroke();
                    break;
                }
            }
        };

        this._reverseLookup = (x, y) => {
            let MAX_DISTANCE = 50;

            let closeIndexes = [];
            for (let i = 0; i <= points.length; i++) {
                if (Math.abs(getX(points[i]) - x) <= MAX_DISTANCE) {
                    closeIndexes.push(i);
                }
            }

            let closest = null;

            if (closeIndexes.length > 0) {
                let closestDist = Infinity;

                let distance = (pos0, pos1) => Math.sqrt(
                    Math.pow(pos0[0] - pos1[0], 2) +
                    Math.pow(pos0[1] - pos1[1], 2)
                );

                for (let line of lines) {
                    for (let i of closeIndexes) {
                        let dist = distance([line[i].x, line[i].y], [x, y]);
                        if (dist < MAX_DISTANCE && dist < closestDist) {
                            let item = {
                                [groupBy]: line.group,
                                [xKey]: line[i].name,
                                [yKey]: line[i].value
                            };

                            if (!groupBy || groupBy == "") {
                                delete item[groupBy];
                            }

                            closest = {item};
                            closestDist = dist;
                        }
                    }
                }
            }

            return closest;
        };
    }
}


const LineChartSelector = props => {
    const {stack} = props;
    const Lines = stack ? StackedLines : OverlappingLines;
    return <Lines {...props} />;
};

LineChartSelector.propTypes = {
    stack: PropTypes.bool
};

export default XYChart(LineChartSelector, {ordinalScale: () => scalePoint(), defaultStack: false});


// WEBPACK FOOTER //
// ./src/kpi/charts/LineChart.jsx